import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate, useLocation } from 'react-router-dom';

import GlobalContext from "./GlobalContext";

const apiUrl = '/wp-json/ucc/v1/directory';
//const apiUrl = 'https://uplandcc.ccsdesigns.com/wp-json/ucc/v1/directory';

const reducer = (state, action) => {
  switch(action.type)
  {
    case 'clear filter':
      return {
        ...state,
        filters: {}
      }
    case 'set category':
      return {
        ...state,
        filters: {
          categories: action.selected
        }
      }
    case 'set error':
      return {
        ...state,
        error: true,
        errorMessage: action.errorMessage
      }
    case 'set filter':
      return {
        ...state,
        filters: { 
          ...state.filters,  
          [action.name]: action.selected
        }
      }
    case 'set filter categories':
      return {
        ...state,
        filterCategories: action.filterCategories,
        initialized: true,
        names: action.names,
        slugs: action.slugs
      }
    case 'set loading':
      return {
        ...state,
        loading: action.value,
        records: []
      }
    case 'set page':
      return {
        ...state,
        page: action.page
      }
    case 'set query':
      return {
        ...state,
        query: action.query
      }
    case 'set records':
      return {
        ...state,
        error: false,
        loading: false,
        numPages: action.numPages,
        records: action.records
      }
    case 'set selected tile':
      return {
        ...state,
        selected: action.id
      }
    default:
  }
}

export default function GlobalState(props) {

  const [ state, dispatch ] = useReducer(reducer, {
    error: false,
    errorMessage: null,
    excludeCategories: [], 
    filterCategories: {}, 
    filterParentCategories: [],
    filters: {
      categories: new Set()
    }, 
    initialized: false,
    limit: 10,
    loading: true,
    names: {},
    numPages: 1,
    order: 'asc',
    orderBy: 'title',
    page: 1,
    query: '',
    records: [],
    selected: null,
    selectedFilters: [],
    slugs: {},
    type: 'works'
  });

  const location = useLocation();
  const navigate = useNavigate();

  const changeFilter = useCallback((options, event) => {
    const selected = options.map(option => option.value);
    dispatch({
      type: 'set filter',
      name: 'categories',
      selected: selected
    });
    const queryString = new URLSearchParams({ 
      cat: selected.join(','),
      query: state.query
    }).toString();
    let newPath = `/search?${queryString}`;
    if(newPath === '/search?cat=&query=') newPath = '/';
    if (location.pathname !== newPath) {
      navigate(newPath);
    }
  }, [dispatch, navigate, location]);
  
  const changeFilterQuery = useCallback(query => {
    dispatch({
      type: 'set query',
      query: query
    });
    const queryString = new URLSearchParams({ 
      cat: state.filters.categories !== null && typeof state.filters.categories === 'object' ? Array.from(state.filters.categories).join(',') : '',
      query: query
    }).toString();
    const newPath = `/search?${queryString}`;
    if (location.pathname !== newPath && newPath !== '/search?cat=&query=') {
      navigate(newPath);
    }
  }, [dispatch, navigate, location]);
  
  const changePage = useCallback((event, page) => {
    dispatch({
      type: 'set page',
      page: page
    });
  }, []);

  const clearFilter = useCallback(() => {
    dispatch({
      type: 'clear filter'
    });
  }, []);

  async function getFilters(){

    const url = `${apiUrl}/filters`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'cache-control': 'no-cache'
      }
    });

    const data = await response.json();

    switch(true){
      case response.status >= 300:
        dispatch({
          type: 'set error',
          errorMessage: 'message' in data ? data.message : response.status
        });
        break;
      default:
        let slugs = {};
        let names = {};

        Object.keys(data.slugs).forEach(slug => {
          const slugData = data.slugs[slug];
          slugs[slug] = slugData.id;
          names[slug] = slugData.name;
        });

        dispatch({
          type: 'set filter categories',
          filterCategories: data.filters,
          names: names,
          slugs: slugs,
        });
    }
  }

  async function getResults(){

    dispatch({
      type: 'set loading',
      value: true
    });

    const filterKeys = Object.keys(state.filters);
    const paramsObj = {
      query: state.query,
      page: state.page,
    }
    
    if(filterKeys.length > 0){
      filterKeys.forEach(filterKey => {
        const filterValue = state.filters[filterKey];
        switch(filterKey){
          case 'categories':
            paramsObj[filterKey] = [...filterValue].join(',');
            break;
          default:
            paramsObj[filterKey] = filterValue;
            break;
        }
      });
    }

    if(paramsObj.categories.length > 0 || paramsObj.query.length > 0){

      const params = new URLSearchParams(paramsObj);
      const url = `${apiUrl}/search?${params}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'cache-control': 'no-cache'
        }
      });

      const data = await response.json();

      switch(true){
        case response.status >= 300:
          dispatch({
            type: 'set error',
            errorMessage: 'message' in data ? data.message : response.status
          });
          break;
        default:
          const main = document.querySelector('header+main');
          const root = document.getElementById('query-display-root');
          const top = main?.offsetTop;

          dispatch({
            type: 'set records',
            numPages: data.pages,
            page: 1,
            records: data.records
          });
          window.scrollTo(0, top);
          root.classList.remove('loading');
      }
    }
  }

  const initialize = () => {
    getFilters();
  }

  const selectTile = useCallback(id => {
    dispatch({
      type: 'set selected tile',
      id: id
    });
  }, []);

  const setCategory = category => {
    dispatch({
      type: 'set category',
      name: 'categories',
      selected: category
    });
  }

  const actions = useMemo(() => ({
    changeFilter: changeFilter,
    changeFilterQuery: changeFilterQuery,
    changePage: changePage,
    clearFilter: clearFilter,
    selectTile: selectTile,
    setCategory: setCategory,
  }), [changeFilter, changeFilterQuery, changePage, selectTile]);

  const globalState = {
    actions: actions,
    names: state.names,
    refs: {},
    state: state,
  }

  useEffect(() => { 
    if(!state.initialized) initialize();
  }, [ state.initialized ]);

  useEffect(() => { 
    if(state.initialized && (Object.keys(state.filters).length > 0 || state.query.length > 0)) getResults();
  }, [ state.initialized, state.categories, state.filters, state.page, state.query ]);

  return (
    <GlobalContext.Provider value={ globalState }>
      {props.children}
    </GlobalContext.Provider>
  );
}